import { HS_FTAS, TODAY } from './fixtures';
import type { ContainerFree, CustomsDecl, DoState, HsCandidate, HsDir, HsFta, ImportShipment } from './types';

export const norm = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

/** Tìm nhanh theo job, khách, tàu, MBL/HBL (không dấu, không phân biệt hoa thường). */
export function searchShipments(rows: readonly ImportShipment[], q: string): ImportShipment[] {
  const n = norm(q.trim());
  return n ? rows.filter((r) => norm(`${r.id} ${r.cus} ${r.vessel} ${r.refs}`).includes(n)) : [...rows];
}

/** DO tự khoá khi khách chưa thanh toán lô, quá hạn công nợ hoặc vượt hạn mức. */
export const isDoLocked = (s: DoState): boolean => s === 'unpaid' || s === 'overdue' || s === 'over';

/** Ngày (số) trong chuỗi dd/MMM/yyyy — dữ liệu mẫu chỉ trong tháng 10, như board. */
const dayOf = (d: string): number => parseInt(d, 10);

export type ShipmentQuick = 'all' | 'lock' | 'eta';
/** Bộ lọc nhanh của tab Hàng nhập: DO bị khoá · ETA trong 3 ngày tới. */
export function quickShipments(rows: readonly ImportShipment[], quick: ShipmentQuick, today = TODAY): ImportShipment[] {
  if (quick === 'lock') return rows.filter((r) => isDoLocked(r.doState));
  if (quick === 'eta') return rows.filter((r) => dayOf(r.eta) >= today && dayOf(r.eta) <= today + 3);
  return [...rows];
}

/** Số ngày free time còn lại theo khách (âm = quá hạn). Cắm điện/DEM-DET theo free của khách, không phải của hãng. */
export const freeLeft = (c: ContainerFree): number => c.freeCustomer - c.used;
export type FreeQuick = 'all' | 'soon' | 'over';
export function quickContainers(rows: readonly ContainerFree[], quick: FreeQuick): ContainerFree[] {
  if (quick === 'soon') return rows.filter((c) => freeLeft(c) >= 0 && freeLeft(c) <= 2);
  if (quick === 'over') return rows.filter((c) => freeLeft(c) < 0);
  return [...rows];
}

export const LANE_LABEL = { G: 'Xanh', Y: 'Vàng', R: 'Đỏ', X: 'Soi chiếu' } as const;
export type LaneQuick = 'all' | 'ry' | 'fee';
export function quickDecls(rows: readonly CustomsDecl[], quick: LaneQuick): CustomsDecl[] {
  if (quick === 'ry') return rows.filter((d) => d.lane !== 'G');
  if (quick === 'fee') return rows.filter((d) => d.offTariff !== 'none');
  return [...rows];
}

// ---- Tư vấn HS ----
/** Họ FTA áp dụng cho xuất xứ này, kèm thuế ưu đãi của ứng viên đang chọn; tốt nhất = thuế thấp nhất. */
export function ftaFor(origin: string, cand: HsCandidate, ftas: readonly HsFta[] = HS_FTAS) {
  const rows = ftas.map((f) => ({ k: f.k, form: f.form, rule: f.rule, ok: f.origins.includes(origin), rate: f.rates[cand.ftaIndex] }));
  const applicable = rows.filter((r) => r.ok);
  const best = [...applicable].sort((a, b) => a.rate - b.rate)[0];
  const mfn = parseFloat(cand.tax[1]);
  const sorted = [...rows].sort((a, b) => Number(b.ok) - Number(a.ok) || a.rate - b.rate).map((r) => ({
    ...r,
    best: !!best && r.k === best.k,
    note: !r.ok ? 'không áp dụng · xuất xứ khác' : best && r.k === best.k ? `Tốt nhất · dùng C/O ${r.form.split(' ')[0]}` : r.rate < mfn ? 'Áp dụng được' : 'Không lợi hơn MFN',
  }));
  return { rows: sorted, best };
}

/** Xuất xứ hợp lệ theo chiều; không hợp lệ thì lấy giá trị đầu (như board). */
export const pickOrigin = (dir: HsDir, origin: string | null | undefined, origins: Record<HsDir, readonly string[]>): string =>
  origins[dir].find((o) => o === origin) ?? origins[dir][0]!;
